import React, { useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import editProfileUser from "assets/images/others/host-side-images/editProfileUSer.png";
import { ReactComponent as EditProfilePersonal } from "assets/images/svgs/edit-profiles.svg";
import AttendeeBreadcrumb from "components/breadcrumbs/AttendeeBreadcrumb";
import { MinorDTO } from "utils/helpers/models/attendee/minor.dto";
import { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import useAuth from "containers/auth/useHooks";
import useAttendee from "../useHooks";
import {
  GENDERS,
  INDUSTRY_TYPES,
  LANGUAGES,
  SKILL_LEVELS,
} from "utils/helpers/enums/shared.enum";
import { warningToaster } from "utils/helpers/common/alert-service";
import { useLocation } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";

const FamilyInformation: React.FC = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.attendeeHome },
    { title: "Family Member Details", path: siteRoutes.familyInformation },
  ];
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    clearErrors,
    trigger,
    setError,
  } = useForm<MinorDTO>();
  const { uploadFile } = useAuth();
  const { addMinorToProfile, updateMinor } = useAttendee();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showRetypePassword, setShowRetypePassword] = useState<boolean>(false);
  const [formData, setFormData] = useState<MinorDTO>(new MinorDTO());
  const profilePicUrlRef = useRef<string | null>(null);
  const location = useLocation();
  const minor = location?.state?.minor ?? {};

  const onSubmit: SubmitHandler<MinorDTO> = async (data) => {
    if (formData.password === formData.retypePassword) {
      if (data.profilePicUrl instanceof File) {
        const file = await uploadFile(data.profilePicUrl);
        const { id = null } = file || {};
        if (id) {
          data.profilePicId = id;
          if (minor?.id) {
            const dataToSend = getFieldsToUpdate(data);
            updateMinor(minor?.id, dataToSend, minor);
          } else {
            addMinorToProfile(data);
          }
        }
      } else {
        if (minor?.id) {
          const dataToSend = getFieldsToUpdate(data);
          updateMinor(minor?.id, dataToSend, minor);
        } else {
          addMinorToProfile(data);
        }
      }
    } else {
      warningToaster("Password Does Not Match");
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      profilePicUrlRef.current = imageUrl;
      setFormData((prev) => ({ ...prev, profilePicUrl: imageUrl }));
      setValue("profilePicUrl", file, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      // Clear error immediately
      clearErrors("profilePicUrl");
      // Revalidate after a short delay to ensure state is updated
      setTimeout(() => {
        trigger("profilePicUrl");
      }, 0);
    }
  };

  const handleChange = (event: any) => {
    const { value, name } = event.target;
    setValue(name, value, { shouldValidate: true });
    setFormData({ ...formData, [name]: value });
  };

  useEffect(() => {
    for (let key in formData) {
      formData[key] = minor[key];
      setValue(key as keyof MinorDTO, formData[key]);
    }

    setFormData({ ...formData });
    // Initialize ref if minor has profile picture
    if (minor?.profilePicUrl) {
      profilePicUrlRef.current = minor.profilePicUrl;
    }
  }, []);

  // Clear profile picture error when image is uploaded
  useEffect(() => {
    if (formData.profilePicUrl) {
      clearErrors("profilePicUrl");
    }
  }, [formData.profilePicUrl, clearErrors]);

  const getFieldsToUpdate = (data: MinorDTO): Partial<MinorDTO> => {
    const fieldsToUpdate: string[] = [
      "firstName",
      "lastName",
      "dob",
      "gender",
      "language",
      "interest",
      "skillLevel",
    ];
    const dataToReturn: Partial<MinorDTO> = {};
    for (let key in data) {
      if (fieldsToUpdate.includes(key)) {
        dataToReturn[key] = data[key];
      }
    }

    return dataToReturn;
  };

  const selectedInterests = watch("interest") || [];

  const toggleInterest = (value) => {
    const updated = selectedInterests.includes(value)
      ? selectedInterests.filter((item) => item !== value)
      : [...selectedInterests, value];

    setValue("interest", updated, { shouldValidate: true });
  };

  // click outside to close dropdown
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="container min-h-screen">
      <AttendeeBreadcrumb links={breadcrumbLinks} />

      <form onSubmit={handleSubmit(onSubmit)} className="">
        <div>
          <div>
            <div className="flex flex-col items-start w-full pb-4">
              <div className="relative">
                <img
                  src={formData.profilePicUrl || editProfileUser}
                  alt="Profile"
                  className="w-36 h-36 rounded-full object-cover"
                />
                <label
                  htmlFor="formData.profilePicUrl"
                  className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
                >
                  <EditProfilePersonal />
                </label>
                <input
                  id="formData.profilePicUrl"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  {...register("profilePicUrl", {
                    validate: (value) => {
                      const hasFile = value instanceof File;
                      const hasImageUrl = !!profilePicUrlRef.current;
                      if (!hasFile && !hasImageUrl && !minor?.id) {
                        return "Minor profile picture is required";
                      }
                      return true;
                    },
                  })}
                  onChange={handleImageChange}
                />
              </div>
              <label
                htmlFor="formData.profilePicUrl"
                className="text-label text-base font-semibold mt-2"
              >
                Minor profile picture
              </label>

              {errors.profilePicUrl && (
                <p className="text-red-500 text-sm">
                  {errors.profilePicUrl.message as string}
                </p>
              )}
            </div>
            <div>
              <div className="w-full my-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    First Name
                  </label>
                  <input
                    type="text"
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...register("firstName", {
                      required: "First name is required",
                    })}
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-sm">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Last Name
                  </label>
                  <input
                    type="text"
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-sm">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    placeholder="Select Date"
                    min={
                      new Date(
                        new Date().setFullYear(new Date().getFullYear() - 18)
                      )
                        .toISOString()
                        .split("T")[0]
                    }
                    max={
                      new Date(
                        new Date().setFullYear(new Date().getFullYear() - 4)
                      )
                        .toISOString()
                        .split("T")[0]
                    }
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...register("dob", {
                      required: "Date of Birth is required",
                      validate: (value) => {
                        if (!value) return true;
                        const selectedDate = new Date(value);
                        const currentDate = new Date();
                        const age =
                          currentDate.getFullYear() -
                          selectedDate.getFullYear();
                        const monthDiff =
                          currentDate.getMonth() - selectedDate.getMonth();
                        const actualAge = monthDiff < 0 ? age - 1 : age;

                        if (actualAge < 4) {
                          return "Age must be at least 4 years old";
                        }
                        if (actualAge >= 18) {
                          return "Age must be less than 18 years old";
                        }
                        return true;
                      },
                    })}
                    value={formData.dob}
                    onChange={handleChange}
                  />
                  {errors.dob && (
                    <p className="text-red-500 text-sm">{errors.dob.message}</p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Gender
                  </label>
                  <select
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...register("gender", {
                      required: "Gender is required",
                    })}
                    value={formData.gender}
                    onChange={handleChange}
                  >
                    <option value="">Select Gender</option>
                    {Object.values(GENDERS).map((gender, index) => {
                      return (
                        <option
                          className="capitalize"
                          value={gender}
                          key={index}
                        >
                          {gender}
                        </option>
                      );
                    })}
                  </select>
                  {errors.gender && (
                    <p className="text-red-500 text-sm">
                      {errors.gender.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Language
                  </label>
                  <select
                    {...register("language", {
                      required: "Language is required",
                    })}
                    value={formData.language}
                    onChange={handleChange}
                    className="w-full h-[51px] px-3 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  >
                    <option value="" disabled>
                      Select Language
                    </option>
                    {Object.values(LANGUAGES).map((lang, index) => {
                      return (
                        <option value={lang} key={index}>
                          {lang}
                        </option>
                      );
                    })}
                  </select>
                  {errors.language && (
                    <p className="text-red-500 text-sm">
                      {errors.language.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Skill Level
                  </label>
                  <select
                    className="w-full h-[51px] px-3 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...register("skillLevel", {
                      required: "Skill Level is required",
                    })}
                    value={formData.skillLevel}
                    onChange={handleChange}
                  >
                    <option value="">Select Skill Level</option>
                    {Object.values(SKILL_LEVELS).map((skill, index) => {
                      return (
                        <option
                          className="capitalize"
                          value={skill}
                          key={index}
                        >
                          {skill.replaceAll("_", " ")}
                        </option>
                      );
                    })}
                  </select>
                  {errors.skillLevel && (
                    <p className="text-red-500 text-sm">
                      {errors.skillLevel.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1 relative" ref={dropdownRef}>
                  <label className="text-label text-base font-semibold">
                    Interest
                  </label>
                  {/* Dropdown Box */}
                  <div
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center w-full h-[51px] px-3 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                  >
                    {selectedInterests.length > 0
                      ? selectedInterests.length >= 3
                        ? `${selectedInterests.length} interest selected`
                        : [...selectedInterests]
                            .map((interest) => interest.replace(/_/g, " "))
                            .sort()
                            .join(", ")
                      : "Select Interest"}
                  </div>
                  {/* Dropdown Menu */}
                  {isOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {Object.values(INDUSTRY_TYPES).map((interest, index) => (
                        <div
                          key={index}
                          onClick={() => toggleInterest(interest)}
                          className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          <span>{interest.replace(/_/g, " ")}</span>
                          {selectedInterests.includes(interest) && (
                            <FaCheck className="text-green-500 text-sm" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    type="hidden"
                    {...register("interest", {
                      required: "Select at least one interest",
                      validate: (value) =>
                        Array.isArray(value) && value.length > 0
                          ? true
                          : "Select at least one interest",
                    })}
                  />

                  {errors.interest && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.interest.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {!minor?.id && (
            <div>
              <span className="text-blackie text-xl font-medium mt-5">
                Minor Credential Information
              </span>
              <div className="w-full my-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Username
                  </label>
                  <input
                    type="text"
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...register("username", {
                      required: "Username is required",
                      minLength: {
                        value: 3,
                        message: "Username must be at least 3 characters",
                      },
                      maxLength: {
                        value: 20,
                        message: "Username must not exceed 20 characters",
                      },
                      validate: (value) => {
                        if (!value) return true;
                        // Allow only alphanumeric characters, '-', '_', and '.'
                        // No spaces or other symbols
                        const usernamePattern = /^[a-zA-Z0-9._-]+$/;
                        if (!usernamePattern.test(value)) {
                          return "Username can only contain letters, numbers, '-', '_'";
                        }
                        // Check for spaces
                        if (/\s/.test(value)) {
                          return "Username cannot contain spaces";
                        }
                        return true;
                      },
                    })}
                    value={formData.username}
                    onChange={handleChange}
                  />
                  {errors.username && (
                    <p className="text-red-500 text-sm">
                      {errors.username.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <label className="text-label text-base font-semibold">
                      Set password
                    </label>
                    <div
                      className="cursor-pointer flex items-center text-label"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <>
                          <AiFillEyeInvisible size={18} className="mr-1" />
                          <span>Hide</span>
                        </>
                      ) : (
                        <>
                          <AiFillEye size={18} className="mr-1" />
                          <span>Show</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      {...register("password", {
                        required: "Password is required",
                        minLength: {
                          value: 8,
                          message:
                            "Password must be at least 8 characters long",
                        },
                        validate: (value) => {
                          if (/\s/.test(value)) {
                            return "Password cannot contain spaces";
                          }
                          if (!/(?=.*[a-z])/.test(value)) {
                            return "Password must include at least one lowercase letter";
                          }
                          if (!/(?=.*[A-Z])/.test(value)) {
                            return "Password must include at least one capital letter";
                          }
                          if (!/(?=.*\d)/.test(value)) {
                            return "Password must include at least one number";
                          }
                          if (!/(?=.*[^\w\s])/.test(value)) {
                            return "Password must include at least one special character";
                          }
                          return true;
                        },
                      })}
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    />
                  </div>
                  {errors.password && (
                    <span className="text-red-500 text-sm">
                      {errors.password.message}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <label className="text-label text-base font-semibold">
                      Retype password
                    </label>
                    <div
                      className="cursor-pointer flex items-center text-label"
                      onClick={() => setShowRetypePassword(!showRetypePassword)}
                    >
                      {showRetypePassword ? (
                        <>
                          <AiFillEyeInvisible size={18} className="mr-1" />
                          <span>Hide</span>
                        </>
                      ) : (
                        <>
                          <AiFillEye size={18} className="mr-1" />
                          <span>Show</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div>
                    <input
                      type={showRetypePassword ? "text" : "password"}
                      placeholder="Retype your password"
                      {...register("retypePassword", {
                        required: "Retyping password is required",
                        validate: (value) => {
                          if (/\s/.test(value)) {
                            return "Password cannot contain spaces";
                          }
                          return true;
                        },
                      })}
                      value={formData.retypePassword}
                      onChange={handleChange}
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    />
                  </div>
                  {errors.retypePassword && (
                    <span className="text-red-500 text-sm">
                      {errors.retypePassword.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            className="px-4 py-2 bg-primaryBlueBg text-white rounded-full"
          >
            {minor?.id ? "Update" : "Create Account"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FamilyInformation;
